import { useCallback, useEffect, useMemo } from "react";
import type React from "react";
import { getOriginalActionIndex, getOriginalStepIndex, getValidCommentActionsAndSteps, getValidRedlineActionsAndSteps } from "./rule-action-plan";

export interface UseActionPlanInputs {
  selectedRuleUuid: string;
  selectBookmarksRange: (bookmarkNames: string[]) => Promise<void>;
  currentCommentActionIndex: number;
  currentCommentStepIndex: number;
  currentActionIndex: number;
  currentStepIndex: number;
  selectedRule: any;
  handleActionApplied: (actionIndex: number, stepIndex: number) => void;
  currentActionPlan: any;
  tabOptions: any[];
  selectedTabOption: string;
  setSelectedTabOption: React.Dispatch<React.SetStateAction<string>>;
}

export function useActionPlan({ selectedRuleUuid, selectBookmarksRange, currentCommentActionIndex, currentCommentStepIndex, currentActionIndex, currentStepIndex, selectedRule, handleActionApplied, currentActionPlan, tabOptions, selectedTabOption, setSelectedTabOption }: UseActionPlanInputs) {
    useEffect(() => {
        if (tabOptions.length > 0 && !tabOptions.includes(selectedTabOption)) {
            setSelectedTabOption(tabOptions[0]);
        }
    }, [selectedRule?.rule?.uuid, tabOptions, setSelectedTabOption]);
    const handleGoToIssue = useCallback(async (bookmarkNames: string[] | undefined) => {
        if (bookmarkNames && bookmarkNames.length > 0) {
            await selectBookmarksRange(bookmarkNames);
        }
    }, [selectBookmarksRange]);
    const handleCommentActionApplied = useCallback(() => {
        if (!currentActionPlan) return;

        const { validActions, validStepsMap } = getValidCommentActionsAndSteps(currentActionPlan);
        const currentCommentAction = validActions[Math.min(currentCommentActionIndex, validActions.length - 1)];
        const validCommentSteps = validStepsMap.get(currentCommentAction) || [];
        const currentCommentStep = validCommentSteps[Math.min(currentCommentStepIndex, validCommentSteps.length - 1)];

        const originalCommentActionIndex = getOriginalActionIndex(currentCommentActionIndex, validActions, currentActionPlan);
        const originalCommentStepIndex = currentCommentStep ? getOriginalStepIndex(currentCommentStepIndex, validCommentSteps, currentCommentAction) : -1;

        handleActionApplied(originalCommentActionIndex, originalCommentStepIndex);
    }, [currentActionPlan, currentCommentActionIndex, currentCommentStepIndex, handleActionApplied]);
    const { validActions: validRedlineActions, validStepsMap: validRedlineStepsMap } = useMemo(() =>
        getValidRedlineActionsAndSteps(currentActionPlan),
        [currentActionPlan]
    );
    const { redlineOoxmls, redlineBookmarks, redlineTargetOoxmlBefores, redlineTextRedline } = useMemo(() => {
        const currentRedlineAction = validRedlineActions[Math.min(currentActionIndex, validRedlineActions.length - 1)];
        const validRedlineSteps = validRedlineStepsMap.get(currentRedlineAction) || [];
        const step = validRedlineSteps[Math.min(currentStepIndex, validRedlineSteps.length - 1)];

        if (!step) {
            return {
                redlineOoxmls: [],
                redlineBookmarks: [],
                redlineTargetOoxmlBefores: [],
                redlineTextRedline: null,
            };
        }

        const ooxmlAfter = step.action_report.target_ooxml_after?.join("") ? [step.action_report.target_ooxml_after.join("")] : [];
        const ooxmlBefore = step.action_report.target_ooxml_before?.join("") ? [step.action_report.target_ooxml_before.join("")] : [];

        return {
            redlineOoxmls: ooxmlAfter,
            redlineBookmarks: step.action_report.target_bookmark_names || [],
            redlineTargetOoxmlBefores: ooxmlBefore,
            redlineTextRedline: step.action_report.target_texts ?? null,
        };
    }, [selectedRuleUuid, currentActionPlan, currentActionIndex, currentStepIndex, selectedTabOption]);
  return { redlineOoxmls, redlineBookmarks, redlineTargetOoxmlBefores, redlineTextRedline, handleCommentActionApplied, handleGoToIssue };
}
